import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Reveal, Overline, SectionHeader } from '../components/Reveal';
import { MarqueeBand } from '../components/MarqueeBand';
import { CTASection } from '../components/CTASection';
import { useSEO } from '../lib/seo';
import { MANIFESTO, VALUES, APPROACH, IMAGES } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

const AboutHero = () => (
    <section className="relative overflow-hidden pt-44 pb-24 sm:pb-32" data-testid="about-hero">
        <div className="absolute inset-0">
            <img src={IMAGES.leadership} alt="Resilience leadership team in session" className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-navy-900/70 via-navy-900/90 to-navy-900" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                <Overline>About Planmodo</Overline>
            </motion.div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
                {['Built by practitioners.', 'Measured by outcomes.'].map((line, i) => (
                    <span key={line} className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: '110%' }}
                            animate={{ y: '0%' }}
                            transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                        >
                            {i === 1 ? <>Measured by <span className="text-deepteal">outcomes.</span></> : line}
                        </motion.span>
                    </span>
                ))}
            </h1>
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
                Planmodo exists to make organizational resilience practical, achievable, and accessible —
                for a fifty-person firm as much as for a global enterprise.
            </motion.p>
        </div>
    </section>
);

const Story = () => (
    <section className="py-20 sm:py-28" data-testid="about-story-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
                <div className="lg:col-span-5">
                    <SectionHeader
                        overline="Our story"
                        title="Ten years of disruptions taught us what actually works."
                    />
                    <Reveal delay={0.1}>
                        <div className="mt-10 space-y-5 text-sm sm:text-base leading-relaxed text-slate-400">
                            <p>
                                Planmodo was founded by practitioners who spent over a decade inside resilience,
                                recovery, and governance roles at global organizations — designing continuity
                                programs, running disaster recovery tests, and coordinating live incident response
                                across healthcare, financial services, technology, manufacturing, and beyond.
                            </p>
                            <p>
                                Again and again, we saw the same pattern: organizations with impressive documentation
                                and very little proven capability. Plans written for auditors. Recovery times that had
                                never been tested. Crisis teams that met for the first time during the crisis.
                            </p>
                            <p>
                                We started Planmodo to close that gap — to deliver resilience work that is measured
                                by what survives contact with reality, and to make that standard accessible to
                                organizations of every size.
                            </p>
                        </div>
                    </Reveal>
                </div>
                <div className="lg:col-span-7">
                    <Reveal delay={0.15}>
                        <div className="relative overflow-hidden rounded-2xl border border-slate-800">
                            <img src={IMAGES.workshop} alt="Executive resilience workshop" className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-7">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-deepteal">Hands-on, always</p>
                                <p className="mt-2 font-display text-lg font-semibold text-slate-100">Every engagement is led by someone who has run a live recovery.</p>
                            </div>
                        </div>
                    </Reveal>
                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Reveal delay={0.2}>
                            <div className="rounded-2xl border border-slate-800 bg-navy-800/50 p-7" data-testid="mission-card">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-deepteal">Mission</p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                                    To make organizational resilience practical, achievable, and accessible — so every
                                    organization can prepare for, respond to, recover from, and adapt to disruption.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.28}>
                            <div className="rounded-2xl border border-slate-800 bg-navy-800/50 p-7" data-testid="vision-card">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-deepteal">Vision</p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                                    A world where readiness is measured in rehearsals, not binders — and where no
                                    organization faces a disruption it was never given the chance to prepare for.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const Manifesto = () => (
    <section className="bg-[#F8FAFC] py-20 sm:py-28 lg:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                dark={false}
                overline="The Planmodo manifesto"
                title="Four principles govern every engagement we take."
            />
            <div className="mt-16 space-y-0">
                {MANIFESTO.map((chapter, i) => (
                    <Reveal key={chapter.num} delay={i * 0.05}>
                        <div
                            className="grid grid-cols-1 gap-4 border-t border-[#E2E8F0] py-10 sm:py-12 md:grid-cols-12 md:gap-8"
                            data-testid={`manifesto-chapter-${chapter.num}`}
                        >
                            <div className="md:col-span-2">
                                <span className="font-mono text-sm text-deepteal">Chapter {chapter.num}</span>
                            </div>
                            <div className="md:col-span-4">
                                <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-ink text-balance">{chapter.title}</h3>
                            </div>
                            <div className="md:col-span-6">
                                <p className="text-sm sm:text-base leading-relaxed text-[#475569]">{chapter.text}</p>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const CoreValues = () => (
    <section className="py-20 sm:py-28 lg:py-32" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                overline="Core values"
                title="How we work when it matters most."
            />
            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {VALUES.map((v, i) => (
                    <Reveal key={v.title} delay={i * 0.06}>
                        <div
                            className="group h-full rounded-2xl border border-slate-800 bg-navy-800/50 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-deepteal/30"
                            data-testid={`value-card-${i}`}
                        >
                            <span className="font-mono text-xs text-slate-500 transition-colors group-hover:text-deepteal">{String(i + 1).padStart(2, '0')}</span>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-50">{v.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Approach = () => (
    <section className="border-t border-slate-800 py-20 sm:py-28" data-testid="approach-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                overline="Our approach"
                title="A continuous loop, not a one-off project."
                description="Resilience decays. Our delivery model treats it as a living capability — assessed, designed, implemented, and validated on a repeating cycle. Every step is aligned with ISO 22301, ISO 27001, and NIST SP 800-34 — standards applied as engineering practice, not paperwork."
            />
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {APPROACH.map((step, i) => (
                    <Reveal key={step.step} delay={i * 0.08}>
                        <div className="relative h-full rounded-2xl border border-slate-800 bg-navy-800/50 p-7" data-testid={`approach-step-${i}`}>
                            <span className="font-display text-4xl font-extrabold text-slate-600">{String(i + 1).padStart(2, '0')}</span>
                            <h3 className="mt-4 font-display text-lg font-semibold text-deepteal">{step.step}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.text}</p>
                            {i < APPROACH.length - 1 && (
                                <span className="absolute -right-4 top-1/2 hidden h-px w-8 bg-glow/30 lg:block" />
                            )}
                        </div>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.2}>
                <div className="mt-12 flex items-start gap-3 rounded-2xl border border-deepteal/15 bg-navy-800/30 p-6">
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-deepteal" />
                    <p className="text-sm leading-relaxed text-slate-300">
                        <span className="font-semibold text-slate-100">Why resilience matters:</span> disruption is no
                        longer a tail risk — it is an operating condition. The organizations that recover fastest are
                        not the ones with the most documentation; they are the ones that have rehearsed.
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);

export default function About() {
    useSEO(
        'About Planmodo — Practitioner-Led Resilience & Recovery',
        'Planmodo was founded by practitioners with over a decade of hands-on experience designing, testing, and running resilience programs for global organizations.'
    );
    return (
        <>
            <AboutHero />
            <MarqueeBand />
            <Story />
            <Manifesto />
            <CoreValues />
            <Approach />
            <CTASection
                title="Work with people who have done the work."
                text="Tell us about your organization and where your resilience program stands today. A practitioner — not a salesperson — will respond."
            />
        </>
    );
}
